import { createPortal } from 'react-dom';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle, Description } from '@headlessui/react';
import SharedButton from '../Button/Button';
import { SharedDialogProps } from '../../types';
import clsx from 'clsx';

/** Above navbar (1001), cookie banner (1000001) and app overlays */
const MODAL_Z = 1000050;

function SharedDialog({
  isOpen,
  onClose,
  title,
  description,
  children,
  onConfirm,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  hasConfirmBtn,
  hasCancelBtn = true,
  size = 'md',
  className = '',
}: SharedDialogProps) {
  const baseStyles = 'overflow-y-auto space-y-4 border bg-white p-12 rounded-md shadow-xl w-full';
  const sizeStyles =
    {
      xs: 'max-w-2xl',
      sm: 'max-w-3xl',
      md: 'max-w-4xl',
      lg: 'max-w-6xl',
      xl: 'max-w-8xl',
    }[size] ?? 'max-w-4xl';

  if (typeof document === 'undefined' || !isOpen) {
    return null;
  }

  return createPortal(
    <Dialog open={isOpen} onClose={onClose} className="relative" style={{ zIndex: MODAL_Z }}>
      <DialogBackdrop className="fixed inset-0 bg-black/40 transition-opacity" style={{ zIndex: MODAL_Z }} />
      <div className="fixed inset-0 overflow-y-auto" style={{ zIndex: MODAL_Z }}>
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className={clsx(baseStyles, sizeStyles, className)} style={{ maxHeight: '85vh' }}>
            {title && <DialogTitle className="font-bold text-xl text-gray-900">{title}</DialogTitle>}
            {description && <Description className="text-gray-600">{description}</Description>}
            {children && <div>{children}</div>}
            {(hasConfirmBtn || hasCancelBtn) && (
              <div className="flex justify-end gap-1 pt-4">
                {hasConfirmBtn && (
                  <SharedButton variant="gradient" onClick={onConfirm}>
                    {confirmText}
                  </SharedButton>
                )}
                {hasCancelBtn && (
                  <SharedButton variant="secondary" onClick={onClose}>
                    {cancelText}
                  </SharedButton>
                )}
              </div>
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>,
    document.body,
  );
}

export default SharedDialog;
